import { useEffect, useMemo } from 'react';
import type { PortalCycleSummary } from '@shared/portal-api-types';

export function PortalLoadingBlock() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
    </div>
  );
}

export function PortalErrorBlock({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-lg rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
      <h2 className="text-lg font-semibold text-destructive">Portal unavailable</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

export const portalTableClass = 'min-w-full text-sm';
export const portalTableHeadClass = 'bg-muted text-left text-muted-foreground';
export const portalCardClass = 'overflow-hidden rounded-lg border border-border bg-card shadow-sm';
export const portalPageTitleClass = 'text-2xl font-semibold text-foreground';
export const portalPageSubtitleClass = 'text-sm text-muted-foreground';
export const portalLabelClass = 'text-muted-foreground';
export const portalLinkClass = 'text-primary hover:underline';
export const portalPrimaryButtonClass = 'rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90';
export const portalProgressTrackClass = 'h-2 w-24 rounded bg-muted';
export const portalProgressFillClass = 'h-2 rounded bg-primary';

export function PortalInlineQueryError({ message }: { message: string }) {
  return (
    <p className="text-sm text-destructive" role="alert">
      {message}
    </p>
  );
}

function getPortalCycleYearKey(cycle: PortalCycleSummary): string {
  const date = cycle.startDate ?? cycle.endDate;
  const year = date?.match(/^(\d{4})/)?.[1];
  return year ?? 'undated';
}

function sortPortalCycleYearKeys(left: string, right: string): number {
  if (left === 'undated') return 1;
  if (right === 'undated') return -1;
  return Number(right) - Number(left);
}

export function PortalCyclePickerFields({
  cycles,
  selectedCycleId,
  onCycleChange,
  disabled = false,
  errorMessage,
  externalSelection = false,
}: {
  cycles: PortalCycleSummary[];
  selectedCycleId: number | null;
  onCycleChange: (cycleId: number) => void;
  disabled?: boolean;
  errorMessage?: string;
  /** When true, URL/state outside the picker owns invalid-cycle fallback. */
  externalSelection?: boolean;
}) {
  const yearKeys = useMemo(
    () => Array.from(new Set(cycles.map(getPortalCycleYearKey))).sort(sortPortalCycleYearKeys),
    [cycles],
  );
  const selectedCycle = cycles.find((cycle) => cycle.id === selectedCycleId);
  const selectedYearKey = selectedCycle
    ? getPortalCycleYearKey(selectedCycle)
    : (yearKeys[0] ?? '');
  const visibleCycles = cycles.filter((cycle) => getPortalCycleYearKey(cycle) === selectedYearKey);
  const fallbackCycleId = visibleCycles[0]?.id;

  useEffect(() => {
    if (externalSelection) return;
    if (!selectedCycle && fallbackCycleId != null) {
      onCycleChange(fallbackCycleId);
    }
  }, [externalSelection, fallbackCycleId, onCycleChange, selectedCycle]);

  return (
    <>
      <label className="flex flex-col gap-1 text-sm">
        <span className={portalLabelClass}>Year</span>
        <select
          className="rounded border border-border bg-background px-3 py-2"
          value={selectedYearKey}
          disabled={disabled || yearKeys.length === 0}
          onChange={(event) => {
            const firstCycleForYear = cycles.find(
              (cycle) => getPortalCycleYearKey(cycle) === event.target.value,
            );
            if (firstCycleForYear) onCycleChange(firstCycleForYear.id);
          }}
        >
          {yearKeys.map((yearKey) => (
            <option key={yearKey} value={yearKey}>
              {yearKey === 'undated' ? 'Date unavailable' : yearKey}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className={portalLabelClass}>Cycle</span>
        <select
          className="min-w-[180px] rounded border border-border bg-background px-3 py-2"
          value={selectedCycleId ?? ''}
          disabled={disabled || visibleCycles.length === 0}
          onChange={(event) => onCycleChange(Number(event.target.value))}
        >
          {visibleCycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.name}
            </option>
          ))}
        </select>
        {errorMessage ? <PortalInlineQueryError message={errorMessage} /> : null}
      </label>
    </>
  );
}
